import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * FormPreviewRenderer - Renders a live form preview inside an iframe
 * 
 * This component is loaded at /forms/:id/preview and communicates with
 * the parent FormBuilder via postMessage to receive form data and theme updates.
 * It renders the form exactly as respondents will see it, with full theme support.
 */

const DEFAULT_THEME = {
  preset: '',
  backgroundColor: '#f9fafb',
  formBackgroundColor: '#ffffff',
  primaryColor: '#111827',
  textColor: '#111827',
  labelColor: '#111827',
  inputBorderColor: '#e5e7eb',
  inputBorderRadius: '0',
  buttonBorderRadius: '8',
  buttonTextColor: '#ffffff',
  fontFamily: '',
  headingFontFamily: '',
  fontSize: '16',
  fieldSpacing: '24',
  formWidth: '100',
  formMaxWidth: '672',
  formPadding: '24',
  layout: 'single',
};

/**
 * Resolves formWidth for backward compatibility.
 * Legacy values are pixel numbers (e.g. '672'), new values are percentages (e.g. '100').
 * If the value is > 100, it's a legacy pixel value — treat as 100% width with that value as maxWidth.
 */
function resolveFormWidth(theme) {
  const rawWidth = getThemeValue(theme, 'formWidth');
  const rawMaxWidth = theme?.formMaxWidth || null;
  const numWidth = parseInt(rawWidth);

  // Legacy pixel value (> 100 means it's px, not %)
  if (numWidth > 100) {
    return { width: '100%', maxWidth: `${numWidth}px` };
  }

  const widthPercent = `${numWidth || 100}%`;
  const maxWidth = rawMaxWidth && rawMaxWidth !== 'none'
    ? `${rawMaxWidth}px`
    : 'none';

  return { width: widthPercent, maxWidth };
}

function getThemeValue(theme, key) {
  return theme?.[key] || DEFAULT_THEME[key];
}

function getFontFamilyCSS(fontFamily) {
  if (!fontFamily || fontFamily === 'System Default') {
    return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  }
  return `"${fontFamily}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
}

function getGoogleFontsUrl(theme) {
  const fonts = [];
  if (theme?.fontFamily && theme.fontFamily !== 'System Default') {
    fonts.push(theme.fontFamily);
  }
  if (theme?.headingFontFamily && theme.headingFontFamily !== 'System Default' && theme.headingFontFamily !== theme.fontFamily) {
    fonts.push(theme.headingFontFamily);
  }
  if (fonts.length === 0) return null;
  const families = fonts.map(f => `family=${f.replace(/\s+/g, '+')}:wght@300;400;500;600;700`).join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

// Mini form field renderers for preview
function PreviewInput({ field, theme }) {
  const borderRadius = `${getThemeValue(theme, 'inputBorderRadius')}px`;
  const borderColor = getThemeValue(theme, 'inputBorderColor');
  const fontSize = `${getThemeValue(theme, 'fontSize')}px`;
  const hasBoxBorder = parseInt(getThemeValue(theme, 'inputBorderRadius')) > 0;

  const inputStyle = hasBoxBorder ? {
    width: '100%',
    padding: '12px 16px',
    fontSize: fontSize,
    backgroundColor: 'transparent',
    border: `1.5px solid ${borderColor}`,
    borderRadius: borderRadius,
    outline: 'none',
    color: getThemeValue(theme, 'textColor'),
    transition: 'border-color 0.2s',
  } : {
    width: '100%',
    padding: '12px 0',
    fontSize: fontSize,
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `2px solid ${borderColor}`,
    borderRadius: '0',
    outline: 'none',
    color: getThemeValue(theme, 'textColor'),
    transition: 'border-color 0.2s',
  };

  return (
    <input
      type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
      placeholder={field.placeholder || 'Type your answer here...'}
      style={inputStyle}
      readOnly
    />
  );
}

function PreviewTextarea({ field, theme }) {
  const borderRadius = `${getThemeValue(theme, 'inputBorderRadius')}px`;
  const borderColor = getThemeValue(theme, 'inputBorderColor');
  const fontSize = `${getThemeValue(theme, 'fontSize')}px`;
  const hasBoxBorder = parseInt(getThemeValue(theme, 'inputBorderRadius')) > 0;

  const style = hasBoxBorder ? {
    width: '100%',
    padding: '12px 16px',
    fontSize: fontSize,
    backgroundColor: 'transparent',
    border: `1.5px solid ${borderColor}`,
    borderRadius: borderRadius,
    outline: 'none',
    resize: 'none',
    color: getThemeValue(theme, 'textColor'),
  } : {
    width: '100%',
    padding: '12px 0',
    fontSize: fontSize,
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `2px solid ${borderColor}`,
    borderRadius: '0',
    outline: 'none',
    resize: 'none',
    color: getThemeValue(theme, 'textColor'),
  };

  return (
    <textarea
      placeholder={field.placeholder || 'Type your answer here...'}
      rows={4}
      style={style}
      readOnly
    />
  );
}

function PreviewSelect({ field, theme }) {
  const borderRadius = `${getThemeValue(theme, 'inputBorderRadius')}px`;
  const borderColor = getThemeValue(theme, 'inputBorderColor');
  const fontSize = `${getThemeValue(theme, 'fontSize')}px`;

  return (
    <select
      style={{
        width: '100%',
        padding: '12px 16px',
        fontSize: fontSize,
        backgroundColor: 'transparent',
        border: `1.5px solid ${borderColor}`,
        borderRadius: borderRadius,
        outline: 'none',
        color: getThemeValue(theme, 'textColor'),
        cursor: 'pointer',
      }}
      disabled
    >
      <option>{field.placeholder || 'Select an option...'}</option>
      {(field.options || []).map((opt, i) => (
        <option key={i}>{opt}</option>
      ))}
    </select>
  );
}

function PreviewRadio({ field, theme }) {
  const borderRadius = `${getThemeValue(theme, 'inputBorderRadius')}px`;
  const borderColor = getThemeValue(theme, 'inputBorderColor');
  const primaryColor = getThemeValue(theme, 'primaryColor');
  const fontSize = `${getThemeValue(theme, 'fontSize')}px`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {(field.options || []).map((option, index) => (
        <label
          key={option}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 16px',
            border: `2px solid ${index === 0 ? primaryColor : borderColor}`,
            borderRadius: Math.max(parseInt(borderRadius) || 0, 8) + 'px',
            cursor: 'pointer',
            backgroundColor: index === 0 ? `${primaryColor}10` : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: `2px solid ${index === 0 ? primaryColor : '#d1d5db'}`,
              marginRight: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {index === 0 && (
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: primaryColor,
              }} />
            )}
          </div>
          <span style={{ fontSize: fontSize, fontWeight: '500', color: getThemeValue(theme, 'textColor') }}>
            {option}
          </span>
        </label>
      ))}
    </div>
  );
}

function PreviewCheckbox({ field, theme }) {
  const borderRadius = `${getThemeValue(theme, 'inputBorderRadius')}px`;
  const borderColor = getThemeValue(theme, 'inputBorderColor');
  const fontSize = `${getThemeValue(theme, 'fontSize')}px`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {(field.options || []).map((option, index) => (
        <label
          key={option}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 16px',
            border: `2px solid ${borderColor}`,
            borderRadius: Math.max(parseInt(borderRadius) || 0, 8) + 'px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              border: `2px solid #d1d5db`,
              marginRight: '14px',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: fontSize, fontWeight: '500', color: getThemeValue(theme, 'textColor') }}>
            {option}
          </span>
        </label>
      ))}
    </div>
  );
}

function PreviewPhone({ field, theme }) {
  const borderRadius = `${getThemeValue(theme, 'inputBorderRadius')}px`;
  const borderColor = getThemeValue(theme, 'inputBorderColor');
  const fontSize = `${getThemeValue(theme, 'fontSize')}px`;
  const hasBoxBorder = parseInt(getThemeValue(theme, 'inputBorderRadius')) > 0;

  const inputStyle = hasBoxBorder ? {
    flex: 1,
    padding: '12px 16px',
    fontSize: fontSize,
    backgroundColor: 'transparent',
    border: `1.5px solid ${borderColor}`,
    borderRadius: borderRadius,
    outline: 'none',
    color: getThemeValue(theme, 'textColor'),
  } : {
    flex: 1,
    padding: '12px 0',
    fontSize: fontSize,
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `2px solid ${borderColor}`,
    outline: 'none',
    color: getThemeValue(theme, 'textColor'),
  };

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
      <div style={{
        padding: hasBoxBorder ? '12px 12px' : '12px 8px 12px 0',
        fontSize: fontSize,
        color: getThemeValue(theme, 'textColor'),
        borderBottom: hasBoxBorder ? 'none' : `2px solid ${borderColor}`,
        border: hasBoxBorder ? `1.5px solid ${borderColor}` : undefined,
        borderRadius: hasBoxBorder ? borderRadius : '0',
        whiteSpace: 'nowrap',
      }}>
        +1
      </div>
      <input
        type="tel"
        placeholder={field.placeholder || 'Phone number'}
        style={inputStyle}
        readOnly
      />
    </div>
  );
}

function PreviewHeading({ field, theme }) {
  const textColor = getThemeValue(theme, 'textColor');
  const headingFont = getFontFamilyCSS(theme?.headingFontFamily || theme?.fontFamily);
  const sizeMap = {
    h1: 'clamp(1.75rem, 4vw, 2.5rem)',
    h2: 'clamp(1.4rem, 3vw, 2rem)',
    h3: 'clamp(1.1rem, 2.5vw, 1.5rem)',
  };
  const level = field.headingLevel || 'h2';
  return (
    <div style={{
      fontSize: sizeMap[level] || sizeMap.h2,
      fontWeight: '700',
      color: textColor,
      lineHeight: '1.2',
      fontFamily: headingFont,
    }}>
      {field.content || ''}
    </div>
  );
}

function PreviewParagraph({ field, theme }) {
  const textColor = getThemeValue(theme, 'textColor');
  const bodyFont = getFontFamilyCSS(theme?.fontFamily);
  const fontSize = `${getThemeValue(theme, 'fontSize')}px`;
  return (
    <div style={{
      fontSize,
      color: textColor,
      lineHeight: '1.7',
      fontFamily: bodyFont,
      whiteSpace: 'pre-wrap',
    }}>
      {field.content || ''}
    </div>
  );
}

function PreviewImage({ field }) {
  const alignMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
  const justifyContent = alignMap[field.alignment || 'center'];
  if (!field.imageUrl) {
    return (
      <div style={{ display: 'flex', justifyContent }}>
        <div style={{
          width: '100%',
          maxWidth: '320px',
          aspectRatio: '16/9',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9ca3af',
          fontSize: '14px',
          border: '2px dashed #d1d5db',
        }}>
          No image set
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent }}>
      <img
        src={field.imageUrl}
        alt={field.altText || ''}
        style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', display: 'block' }}
      />
    </div>
  );
}

function PreviewFormField({ field, theme }) {
  const labelColor = getThemeValue(theme, 'labelColor');
  const headingFont = getFontFamilyCSS(theme?.headingFontFamily || theme?.fontFamily);
  const fieldSpacing = `${getThemeValue(theme, 'fieldSpacing')}px`;

  if (field.type === 'hidden') return null;

  // Content fields — render without an input label wrapper
  if (field.type === 'heading') {
    return <div style={{ marginBottom: fieldSpacing }}><PreviewHeading field={field} theme={theme} /></div>;
  }
  if (field.type === 'paragraph') {
    return <div style={{ marginBottom: fieldSpacing }}><PreviewParagraph field={field} theme={theme} /></div>;
  }
  if (field.type === 'image') {
    return <div style={{ marginBottom: fieldSpacing }}><PreviewImage field={field} /></div>;
  }

  const labelStyle = {
    display: 'block',
    fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
    fontWeight: '500',
    color: labelColor,
    marginBottom: '20px',
    lineHeight: '1.3',
    fontFamily: headingFont,
  };

  let fieldContent;
  switch (field.type) {
    case 'text':
    case 'email':
    case 'number':
    case 'date':
      fieldContent = <PreviewInput field={field} theme={theme} />;
      break;
    case 'textarea':
      fieldContent = <PreviewTextarea field={field} theme={theme} />;
      break;
    case 'phone':
      fieldContent = <PreviewPhone field={field} theme={theme} />;
      break;
    case 'select':
      fieldContent = <PreviewSelect field={field} theme={theme} />;
      break;
    case 'radio':
      fieldContent = <PreviewRadio field={field} theme={theme} />;
      break;
    case 'checkbox':
      fieldContent = <PreviewCheckbox field={field} theme={theme} />;
      break;
    default:
      return null;
  }

  return (
    <div style={{ marginBottom: fieldSpacing }}>
      <label style={labelStyle}>
        {field.label}
        {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
      </label>
      {fieldContent}
    </div>
  );
}

export default function FormPreviewRenderer() {
  const [form, setForm] = useState(null);
  const [steps, setSteps] = useState([]);
  const [settings, setSettings] = useState({});
  const [, setActiveBreakpoint] = useState('desktop');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const containerRef = useRef(null);
  const fontLinkRef = useRef(null);

  const theme = useMemo(() => settings?.theme || {}, [settings?.theme]);

  // Send height to parent
  const sendHeight = useCallback(() => {
    if (containerRef.current) {
      const height = containerRef.current.scrollHeight;
      window.parent.postMessage({ type: 'PREVIEW_HEIGHT', height }, '*');
    }
  }, []);

  // Listen for messages from parent
  useEffect(() => {
    const handleMessage = (event) => {
      const { data } = event;
      if (!data || !data.type) return;

      switch (data.type) {
        case 'UPDATE_FORM_DATA':
          if (data.form) setForm(data.form);
          if (data.steps) setSteps(data.steps);
          if (data.settings) setSettings(data.settings);
          break;
        case 'SET_BREAKPOINT':
          if (data.breakpoint) setActiveBreakpoint(data.breakpoint);
          break;
        case 'SET_CURRENT_STEP':
          if (data.currentStep !== undefined) setCurrentStepIndex(data.currentStep);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    // Notify parent that preview is ready
    window.parent.postMessage({ type: 'PREVIEW_READY' }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Send height updates on data changes
  useEffect(() => {
    // Small delay to allow render to complete
    const timer = setTimeout(sendHeight, 50);
    return () => clearTimeout(timer);
  }, [form, steps, settings, currentStepIndex, sendHeight]);

  // Load Google Fonts when theme changes
  useEffect(() => {
    const url = getGoogleFontsUrl(theme);
    if (url) {
      if (fontLinkRef.current) {
        fontLinkRef.current.href = url;
      } else {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        document.head.appendChild(link);
        fontLinkRef.current = link;
      }
    } else if (fontLinkRef.current) {
      fontLinkRef.current.remove();
      fontLinkRef.current = null;
    }
  }, [theme]);

  // Also observe resize
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => sendHeight());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [sendHeight]);

  const currentStep = steps[currentStepIndex] || steps[0] || { fields: [] };
  const isMultiStep = steps.length > 1;
  const primaryColor = getThemeValue(theme, 'primaryColor');
  const bodyFont = getFontFamilyCSS(theme?.fontFamily);
  const headingFont = getFontFamilyCSS(theme?.headingFontFamily || theme?.fontFamily);

  const backgroundColor = getThemeValue(theme, 'backgroundColor');
  const formBgColor = getThemeValue(theme, 'formBackgroundColor');
  const textColor = getThemeValue(theme, 'textColor');
  const { width: formWidth, maxWidth: formMaxWidth } = resolveFormWidth(theme);
  const formPadding = `${getThemeValue(theme, 'formPadding')}px`;
  const btnBorderRadius = `${getThemeValue(theme, 'buttonBorderRadius')}px`;
  const btnTextColor = getThemeValue(theme, 'buttonTextColor');
  const inputBorderRadius = parseInt(getThemeValue(theme, 'inputBorderRadius'));
  const layout = getThemeValue(theme, 'layout');

  const hasTheme = theme && Object.keys(theme).length > 0;
  const showFormContainer = hasTheme && formBgColor !== backgroundColor;

  if (!steps.length || steps.every(s => !s.fields || s.fields.length === 0)) {
    return (
      <div
        ref={containerRef}
        style={{
          minHeight: '100vh',
          backgroundColor: backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: bodyFont,
        }}
      >
        <div style={{ textAlign: 'center', color: '#9ca3af' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}>
            <path d="M9 12h6m-3-3v6m-7 4h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p style={{ fontSize: '16px', fontWeight: '500' }}>Add fields to see your form preview</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>Fields will appear here as you add them</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: '100vh',
        backgroundColor: backgroundColor,
        fontFamily: bodyFont,
        color: textColor,
      }}
    >
      {/* Progress Bar */}
      {isMultiStep && (
        <div style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #f3f4f6',
        }}>
          <div style={{ width: '100%', height: '4px', backgroundColor: '#f3f4f6' }}>
            <div style={{
              height: '100%',
              width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
              backgroundColor: primaryColor,
              transition: 'width 0.5s ease-out',
            }} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: isMultiStep ? 'calc(100vh - 5px)' : '100vh',
        padding: '32px 16px',
      }}>
        <div style={{
          width: formWidth,
          maxWidth: formMaxWidth,
          margin: '0 auto',
          padding: showFormContainer ? formPadding : '0',
          backgroundColor: showFormContainer ? formBgColor : 'transparent',
          borderRadius: showFormContainer ? `${Math.max(inputBorderRadius, 12)}px` : '0',
          boxShadow: showFormContainer ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)' : 'none',
        }}>
          {/* Organization Logo */}
          {settings?.show_org_logo && settings?.org_logo_url && (
            <div style={{ marginBottom: '40px' }}>
              <img
                src={settings.org_logo_url}
                alt={`${settings.org_name || 'Organization'} logo`}
                style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
              />
            </div>
          )}

          {/* Form Title */}
          {settings?.show_form_title && form?.title && (
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h1 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                fontWeight: '700',
                color: textColor,
                marginBottom: '12px',
                lineHeight: '1.2',
                fontFamily: headingFont,
              }}>
                {form.title}
              </h1>
              {form.description && (
                <p style={{
                  fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                  color: '#6b7280',
                  lineHeight: '1.6',
                  maxWidth: '540px',
                  margin: '0 auto',
                }}>
                  {form.description}
                </p>
              )}
            </div>
          )}

          {/* Step Title (for steps beyond the first) */}
          {currentStep?.title && !settings?.hide_step_header_text && (
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <h2 style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                fontWeight: '700',
                color: textColor,
                lineHeight: '1.2',
                fontFamily: headingFont,
              }}>
                {currentStep.title}
              </h2>
            </div>
          )}

          {/* Form Fields */}
          <div style={{
            display: layout === 'two-column' ? 'grid' : 'flex',
            gridTemplateColumns: layout === 'two-column' ? 'repeat(2, 1fr)' : undefined,
            gap: layout === 'two-column' ? '16px 24px' : undefined,
            flexDirection: layout !== 'two-column' ? 'column' : undefined,
          }}>
            {(currentStep?.fields || []).map(field => (
              <PreviewFormField key={field.id} field={field} theme={theme} />
            ))}
          </div>

          {/* Navigation Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            paddingTop: '28px',
            marginTop: '8px',
          }}>
            <button
              disabled
              style={{
                width: '100%',
                padding: '14px 28px',
                borderRadius: btnBorderRadius,
                fontSize: `${parseInt(getThemeValue(theme, 'fontSize')) || 16}px`,
                fontWeight: '600',
                color: btnTextColor,
                backgroundColor: primaryColor,
                border: 'none',
                cursor: 'default',
                boxShadow: `0 4px 14px 0 ${primaryColor}30`,
                transition: 'all 0.2s',
                fontFamily: bodyFont,
              }}
            >
              {isMultiStep && currentStepIndex < steps.length - 1
                && currentStep?.defaultNextStepId !== '__submit__'
                && !(currentStep?.conditions || []).some(c => c.targetStepId === '__submit__')
                ? (settings?.button_text_next || 'Continue \u2192')
                : (settings?.button_text_submit || '\u2713 Submit')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
